import { StringHHmm } from '../models/date-time';
import { OccupiedSlot } from '../models/occupied-slot';
import { ClassifiedSlot, SlotAvailability } from '../models/slot-availability';
import { toStringHHmm } from './date-type-functions';

export interface ClassifySlotsParams {
  slots: StringHHmm[];
  occupiedSlots: OccupiedSlot[];
  activityDurationMap: Map<string, number>;
  selectedActivityId?: string;
  selectedActivityDuration?: number;
}

export class SlotUtils {
  public static stringHHmmToMinutes(stringHHmm: StringHHmm): number {
    const hours = parseInt(stringHHmm.substring(0, 2), 10);
    const minutes = parseInt(stringHHmm.substring(2, 4), 10);
    return hours * 60 + minutes;
  }

  public static minutesToStringHHmm(minutes: number | undefined | null): StringHHmm {
    if (minutes == null) {
      return toStringHHmm('0000');
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return toStringHHmm(String(hours).padStart(2, '0') + String(mins).padStart(2, '0'));
  }

  public static calculateBusySlots(
    allSlots: StringHHmm[],
    occupiedSlots: OccupiedSlot[],
    activityDurationMap: Map<string, number>,
    selectedActivityDuration?: number,
  ): Set<StringHHmm> {
    const occupiedTimes = new Set<StringHHmm>(occupiedSlots.map((s) => s.time));
    const busySlots = new Set<StringHHmm>();
    for (const occupied of occupiedSlots) {
      const occupiedMinutes = SlotUtils.stringHHmmToMinutes(occupied.time);

      // post-busy: slots that fall within the ongoing booked activity's duration
      const bookedDuration = activityDurationMap.get(occupied.activityId);
      if (bookedDuration) {
        for (const slot of allSlots) {
          if (occupiedTimes.has(slot)) continue;
          const slotMinutes = SlotUtils.stringHHmmToMinutes(slot);
          if (slotMinutes > occupiedMinutes && slotMinutes < occupiedMinutes + bookedDuration) {
            busySlots.add(slot);
          }
        }
      }

      // pre-busy: slots where starting the selected activity would overlap this booked slot
      if (selectedActivityDuration) {
        for (const slot of allSlots) {
          if (occupiedTimes.has(slot)) continue;
          const slotMinutes = SlotUtils.stringHHmmToMinutes(slot);
          if (slotMinutes < occupiedMinutes && slotMinutes + selectedActivityDuration > occupiedMinutes) {
            busySlots.add(slot);
          }
        }
      }
    }
    return busySlots;
  }

  public static calculateNotEnoughTimeSlots(
    allSlots: StringHHmm[],
    occupiedSlots: OccupiedSlot[],
    activityDurationMap: Map<string, number>,
    selectedActivityId: string | undefined,
  ): Set<StringHHmm> {
    const occupiedTimes = new Set<StringHHmm>(occupiedSlots.map((s) => s.time));
    const notEnoughTimeSlots = new Set<StringHHmm>();
    for (const occupied of occupiedSlots) {
      const occupiedMinutes = SlotUtils.stringHHmmToMinutes(occupied.time);

      // pre-busy (not enough time): slots where starting the selected activity would overlap a booked slot
      const selectedDuration = selectedActivityId ? activityDurationMap.get(selectedActivityId) : 0;
      if (selectedDuration) {
        for (const slot of allSlots) {
          if (occupiedTimes.has(slot)) continue;
          const slotMinutes = SlotUtils.stringHHmmToMinutes(slot);
          if (slotMinutes < occupiedMinutes && slotMinutes + selectedDuration > occupiedMinutes) {
            notEnoughTimeSlots.add(slot);
          }
        }
      }
    }
    return notEnoughTimeSlots;
  }

  public static generateTimeSlots(
    startTime: StringHHmm,
    endTime: StringHHmm,
    slotInterval: number,
    excludedSlots?: StringHHmm[],
  ): StringHHmm[] {
    if (!startTime || !endTime || !slotInterval) {
      return [];
    }
    const slots: StringHHmm[] = [];
    const startMinutes = SlotUtils.stringHHmmToMinutes(startTime);
    const endMinutes = SlotUtils.stringHHmmToMinutes(endTime);

    for (let currentMinutes = startMinutes; currentMinutes <= endMinutes; currentMinutes += slotInterval) {
      slots.push(SlotUtils.minutesToStringHHmm(currentMinutes));
    }

    if (excludedSlots && excludedSlots.length > 0) {
      return slots.filter((slot: StringHHmm) => !excludedSlots.includes(slot));
    }

    return slots;
  }

  /**
   * Classifies each slot into a canonical {@link SlotAvailability}, applying the shared
   * booked / busy / not-enough-time rules in a single, consistent priority order.
   * Consumers overlay their own presentation- or time-relative states (e.g. "too late").
   */
  public static classifySlots(params: ClassifySlotsParams): ClassifiedSlot[] {
    const { slots, occupiedSlots, activityDurationMap, selectedActivityId, selectedActivityDuration } = params;
    const occupiedTimes = new Set<StringHHmm>(occupiedSlots.map((s) => s.time));
    const busySlots = SlotUtils.calculateBusySlots(slots, occupiedSlots, activityDurationMap, selectedActivityDuration);
    const notEnoughTimeSlots = SlotUtils.calculateNotEnoughTimeSlots(slots, occupiedSlots, activityDurationMap, selectedActivityId);
    return slots.map((time: StringHHmm) => ({
      time,
      availability: SlotUtils.resolveAvailability(time, occupiedTimes, busySlots, notEnoughTimeSlots),
    }));
  }

  private static resolveAvailability(
    time: StringHHmm,
    occupiedTimes: Set<StringHHmm>,
    busySlots: Set<StringHHmm>,
    notEnoughTimeSlots: Set<StringHHmm>,
  ): SlotAvailability {
    if (occupiedTimes.has(time)) return 'booked';
    if (busySlots.has(time)) return 'busy';
    if (notEnoughTimeSlots.has(time)) return 'notEnoughTime';
    return 'available';
  }
}
